import type { Proposta, PropostaItem } from '@/types'
import { formatCurrency } from '@/utils/format-currency'
import {
  calculateAllVariations,
  type LayoutConfig,
  type PlanServiceItem,
} from '@/utils/dealCalculations'
import { withComboPricingLayout } from '@/utils/comboSlideLayout'
import { formatNegotiationDeadlineSlideLabel } from '@/utils/negotiationDeadline'
import { resolvePropostaItems } from '@/utils/normalizeProposta'
import { buildSlideLayoutForProposalItem } from '@/utils/planCatalog'
import {
  formatSupportHoursSlideLabel,
  resolveSupportHoursForSlide,
  resolveSupportTierSlideLines,
} from '@/utils/planSuportePricing'
import { resolveProposalLayout } from '@/utils/proposalExport'
import { parseComboVariationsFromDados } from '@/components/Deals/DealDetailPanel/DealProposalTab/proposalStudioSave'
import {
  buildSlideOptions,
  computeActivePlanContext,
  flattenComboSlides,
} from '@/components/Deals/ProposalStudio/computeActivePlanContext'

/** Dados do slide de valor, no mesmo formato que o Proposal Studio exporta. */
export type PublicPresentationSlide = {
  layout: LayoutConfig
  planName: string
  planDescription: string
  supportHoursLabel: string
  supportTierLines: string[]
  planTipo: string
  projectType?: string
  planValue: number
  isRecurring: boolean
  hidePricing: boolean
  priceCompareFrom?: number
  paymentDetails: string
  loyaltyDuration: string
  negotiationDeadlineLabel: string
  bonusText: string
  serviceGroups: Map<string, PlanServiceItem[]>
}

function slideItemAt(
  items: PropostaItem[],
  comboCount: number,
  variations: ReturnType<typeof calculateAllVariations>,
  index: number,
): PropostaItem | undefined {
  if (comboCount > 0) return flattenComboSlides(variations)[index]?.item
  return items[index] ?? items[0]
}

function slideDescription(plan: PropostaItem, layout: LayoutConfig): string {
  const own = String(plan.description || '').trim()
  if (own) return own
  const fromLayout = String(layout.description || '').trim()
  const name = String(plan.descricao || plan.nome || '').trim()
  if (!fromLayout || fromLayout === name) return ''
  return fromLayout
}

function toSlide(
  plan: PropostaItem,
  showPricing: boolean,
  comboPriceFrom: number | undefined,
  layout: LayoutConfig,
  serviceGroups: Map<string, PlanServiceItem[]>,
): PublicPresentationSlide {
  return {
    layout,
    planName: plan.descricao || plan.nome || '',
    planDescription: slideDescription(plan, layout),
    supportHoursLabel: formatSupportHoursSlideLabel(resolveSupportHoursForSlide(plan, [])),
    supportTierLines: resolveSupportTierSlideLines(plan, []),
    planTipo: plan.tipo || 'plano',
    projectType: plan.projectType,
    planValue: showPricing ? plan.pricing?.installmentValue ?? plan.valorUnitario ?? 0 : 0,
    isRecurring: true,
    hidePricing: !showPricing,
    priceCompareFrom: showPricing ? comboPriceFrom : undefined,
    paymentDetails:
      showPricing && !plan.pricing?.recorrente
        ? `${plan.pricing?.installments || 1}x de ${formatCurrency(plan.pricing?.installmentValue || plan.valorUnitario)}`
        : '',
    loyaltyDuration: showPricing
      ? plan.pricing?.recorrente
        ? `Fidelidade: ${plan.duration || 12} meses`
        : `Duração: ${plan.duration || 1} meses`
      : '',
    negotiationDeadlineLabel: showPricing
      ? formatNegotiationDeadlineSlideLabel(plan.negotiationValidUntil)
      : '',
    bonusText: showPricing && plan.showBonus === true ? plan.bonus || '' : '',
    serviceGroups,
  }
}

/** Monta os slides de valor que o “Exportar PDF” do estúdio acrescenta ao deck. */
export function buildPublicPresentationSlides(proposta: Proposta): PublicPresentationSlide[] {
  const items = resolvePropostaItems(proposta)
  if (items.length === 0) return []

  const comboVariations = parseComboVariationsFromDados(proposta)
  const variations = calculateAllVariations(comboVariations, items)
  const count = buildSlideOptions(comboVariations, items, variations).length
  const savedLayout = resolveProposalLayout(proposta)

  return Array.from({ length: count }, (_, index) => {
    const ctx = computeActivePlanContext({
      comboVariations,
      proposalItems: items,
      variationsWithPricing: variations,
      activeIndex: index,
      catalogPlans: [],
    })
    const source = slideItemAt(items, comboVariations.length, variations, index)
    const plan = ctx.activePlan ?? source
    if (!plan) return null

    let layout = buildSlideLayoutForProposalItem(source ?? plan, [], savedLayout)
    if (comboVariations.length > 0 && ctx.showPricing) {
      layout = withComboPricingLayout(layout, plan.descricao || plan.nome)
    }

    return toSlide(plan, ctx.showPricing, ctx.comboPriceFrom, layout, ctx.activeServiceGroups)
  }).filter((slide): slide is PublicPresentationSlide => slide !== null)
}
